<template>
    <div class="map-schedules-route-schedule">
        <!-- Filters -->
        <div class="flex flex-col space-y-4 items-left md:justify-left md:inline-flex md:flex-row md:space-x-4 md:space-y-0">
            <!-- Time of Travel -->
            <div>
                <div class="text-sm flex font-normal text-gray-600 justify-between mb-1">
                    <div>Day of Travel</div>
                    <div v-if="routeStore.filters.dayOfTravel.selectedDate" >
                        <label for="date-picker-button">Pick a date</label>
                    </div>
                </div>

                <div role="group" aria-labelledby="day-of-travel-label" class="flex flex-col overflow-visible border border-gray-200 divide-y divide-gray-200 rounded-lg md:inline-flex md:flex-row md:items-center md:justify-left md:divide-y-0 md:divide-x">
                    <template v-for="option in routeStore.filters.dayOfTravel.options">
                        <button
                            type="button"
                            role="radio"
                            :aria-checked="routeStore.filters.dayOfTravel.selected === option.value"
                            class="p-2 text-sm font-normal day-of-travel-btn first:rounded-l-lg focus:outline-none ring-2 ring-transparent focus:ring-secondary focus:ring-offset-2 focus:ring-offset-white"
                            :class="[
                                routeStore.filters.dayOfTravel.selected === option.value
                                    ? 'bg-secondary text-white focus:bg-secondary'
                                    : 'text-gray-700 bg-transparent',
                                'border-transparent hover:border-secondary',
                            ]"
                            @click="routeStore.setDayOfTravelValue(option)"
                        >
                            {{ option.label }}
                        </button>
                    </template>
                    <DatePicker ref="dayOfTravelPicker" id="day_of_travel" class="z-50" color="vm-orange"
                                :modelValue="routeStore.filters.dayOfTravel.selectedDate"
                                @popoverDidShow="onDatePickerPopoverOpen"
                                @popoverDidHide="onDatePickerPopoverClose"
                                @update:modelValue="setDate"
                    >
                        <template #header-prev-button>
                            <svg stroke-linecap="round" stroke-linejoin="round" viewBox="0 0 24 24" width="24" height="24" class="vc-base-icon">
                                <polyline points="15 18 9 12 15 6"></polyline>
                            </svg>
                            <span class="sr-only">Previous month</span>
                        </template>
                        <template #header-next-button>
                            <svg stroke-linecap="round" stroke-linejoin="round" viewBox="0 0 24 24" width="24" height="24" class="vc-base-icon">
                                <polyline points="9 18 15 12 9 6"></polyline>
                            </svg>
                            <span class="sr-only">Next month</span>
                        </template>
                        <template #nav-prev-button>
                            <svg stroke-linecap="round" stroke-linejoin="round" viewBox="0 0 24 24" width="22px" height="24px" class="vc-base-icon">
                                <polyline points="15 18 9 12 15 6"></polyline>
                            </svg>
                            <span class="sr-only">Previous year</span>
                        </template>
                        <template #nav-next-button>
                            <svg stroke-linecap="round" stroke-linejoin="round" viewBox="0 0 24 24" width="22px" height="24px" class="vc-base-icon">
                                <polyline points="9 18 15 12 9 6"></polyline>
                            </svg>
                            <span class="sr-only">Next year</span>
                        </template>
                        <template v-slot="{ togglePopover }">
                            <div class="flex items-center">
                                <button
                                    class="w-full p-2 text-sm font-normal rounded-r-lg pick-a-date-btn ring-2 ring-transparent focus:outline-none focus:ring-secondary focus:ring-offset-2 focus:ring-offset-white"
                                    :class="routeStore.filters.dayOfTravel.selectedDate !== null ? 'bg-secondary text-white hover:bg-secondary-900' : 'text-gray-700 bg-transparent'"
                                    @click="togglePopover"
                                    :aria-label="routeStore.filters.dayOfTravel.selectedDate ? `Pick a date - ${routeStore.filters.dayOfTravel.selected}` : 'Pick a date'"
                                    id="date-picker-button"
                                    :aria-expanded="calendarIsShowing"
                                >
                                    <template v-if="routeStore.filters.dayOfTravel.selectedDate">
                                        {{ routeStore.filters.dayOfTravel.selected }}
                                    </template>
                                    <template v-else>
                                        Or pick a date
                                    </template>
                                </button>
                            </div>
                        </template>
                    </DatePicker>
                </div>
            </div>

            <!-- Direction of Travel -->
            <div v-if="routeStore.filters.directionOfTravel.options.length > 0">
                <div id="direction-of-travel-label" class="block mb-1 text-sm font-normal tracking-wide text-gray-600">
                    Direction of Travel
                </div>
                <div role="group" aria-labelledby="direction-of-travel-label" class="flex flex-col overflow-visible border border-gray-200 divide-y divide-gray-200 rounded-lg md:inline-flex md:flex-row md:items-center md:justify-left md:divide-x md:divide-y-0">
                    <template v-for="option in routeStore.filters.directionOfTravel.options">
                        <button
                            type="button"
                            role="radio"
                            :aria-checked="routeStore.filters.directionOfTravel.selected === option.value"
                            class="p-2 text-sm font-normal direction-of-travel-btn ring-2 ring-transparent first:rounded-l-lg last:rounded-r-lg focus:outline-none focus:ring-secondary focus:ring-offset-2 focus:ring-offset-white"
                            :class="routeStore.filters.directionOfTravel.selected === option.value
                                ? 'bg-secondary text-white'
                                : 'text-gray-700 bg-transparent'"
                            @click="routeStore.setDirectionOfTravelValue(option.value)"
                        >
                            {{ option.label }}
                        </button>
                    </template>
                </div>
            </div>
        </div>

        <div role="region" class="loadingSpinnerDiv" aria-live="polite">
            <div v-if="!currentRoute || !currentRoute.schedule" class="flex justify-center w-full py-8">
                <Spinner class="w-8 h-8 text-primary" />
            </div>
        </div>
        <div v-if="currentRoute && selectedSchedule" class="relative mt-8 lg:-ml-2 lg:-mr-2">
            <div>
                <div class="lg:pl-16">
                    <h3 role="presentation" class="text-3xl font-normal text-primary">
                        {{ routeStore.filters.dayOfTravel.selected }}
                    </h3>
                    <h3 class="text-2xl font-normal text-primary">
                        {{ routeStore.filters.directionOfTravel.selectedLabel }} Service
                    </h3>
                    <div v-if="!routeStore.filters.dayOfTravel.selectedDate && selectedSchedule.effectiveDate?.isFuture"
                        class="flex items-center my-2 text-sm font-normal text-gray-900"
                        role="alert"
                        :aria-label="`Schedule for ${routeStore.filters.dayOfTravel.selected} ${routeStore.filters.directionOfTravel.selected} is effective from ${selectedSchedule.effectiveDate.formattedFromDate} to ${selectedSchedule.effectiveDate.formattedToDate}.`"
                    >
                        <Icon :icon="mdiAlert" class="w-6 h-6 mr-2 text-yellow-500" role="none" />
                        Effective from {{ selectedSchedule.effectiveDate.formattedFromDate }} to {{ selectedSchedule.effectiveDate.formattedToDate }}
                    </div>
                </div>

                <div v-if="selectedSchedule.empty" class="flex items-center justify-center w-full px-8 py-32 border-t-2 border-b-2 border-primary">
                    <div class="leading-tight prose prose-a:underline prose-a:text-secondary hover:prose-a:text-secondary-darker">
                        <div v-if="noServiceMessageHTML?.length" v-html="noServiceMessageHTML"></div>
                        <div v-if="noServiceMessageHTMLSpanish?.length" v-html="noServiceMessageHTMLSpanish" lang="es"></div>
                    </div>
                </div>
                <div v-else class="relative flex flex-col-reverse w-full border-t-2 border-b-2 lg:flex-row border-primary">
                    <span v-if="!selectedSchedule.empty" class="absolute left-[3rem] top-[-7px] w-3 h-3 bg-primary border-2 border-primary rounded-full z-10"></span>
                    <div class="relative flex w-full lg:w-[24rem] h-[51vh] overflow-hidden">
                        <div v-if="routeScroll.showTop" class="absolute top-0 z-10 left-3 text-primary blink-top">
                            <Icon class="w-8 h-8" :icon="mdiArrowUp" aria-label="Arrow up icon"/>
                        </div>

                        <MapsSchedulesRouteScheduleList :schedule="selectedSchedule"/>

                        <div v-if="routeScroll.showBottom"
                             class="absolute bottom-0 z-10 left-3 text-primary blink-bottom">
                            <Icon class="w-8 h-8" :icon="mdiArrowDown" aria-label="Arrow down icon"/>
                        </div>
                    </div>
                    <div class="flex-grow h-[51vh] min-h-[300px]">
                        <div class="w-full h-full overflow-hidden">
                            <RouteMap
                                v-if="currentRoute && selectedSchedule.shape"
                                class="w-full h-full"
                                :route="currentRoute"
                                :shape="mapShape"
                                :highlighted-stops="selectedStops"
                                @ready="onMapReady"
                                @onMarkerClicked="onMarkerClicked"
                            />
                        </div>
                    </div>
                </div>

                <MapsSchedulesRouteRunOn :schedule="selectedSchedule" class="mt-8 ml-8 text-sm text-gray-600"/>
            </div>

            <div role="region" class="loadingSpinnerDiv" aria-live="polite">
                <div v-if="routeStore.isProcessingSchedule"
                     class="absolute top-0 left-0 z-50 flex items-center justify-center w-full h-full py-8 bg-gray-50 bg-opacity-90">
                    <Spinner class="w-8 h-8 text-primary" />
                </div>
            </div>
        </div>
        <div v-else class="relative z-50 flex items-center justify-center w-full py-8 bg-gray-50 bg-opacity-90" style="height: 400px">
            <div role="region" class="loadingSpinnerDiv" aria-live="polite">
                <Spinner class="w-8 h-8 text-primary" />
            </div>
        </div>
    </div>
</template>

<script>
import { mdiAlert, mdiArrowDown, mdiArrowUp, mdiTable, mdiViewList } from "@mdi/js";
import { DatePicker } from 'v-calendar';
import {computed, defineComponent, ref} from 'vue';
import { setGoogleApi } from "../../utils/google";
import Icon from "../Icon";
import InputSelect from "../InputSelect";
import LightButton from "../LightButton";
import RouteMap from "../RouteMap";
import Spinner from "../Spinner";
import MapsSchedulesRouteRunOn from "./MapsSchedulesRouteRunOn";
import MapsSchedulesRouteScheduleList from "./MapsSchedulesRouteScheduleList";
import MapsSchedulesStopInfo from "./MapsSchedulesStopInfo";
import { usePage } from "@inertiajs/vue3";
import { useRouteStore } from "../../stores/routes";

export default defineComponent({
    name: 'MapsSchedulesRouteSchedule',
    components: {
        Icon,
        Spinner,
        RouteMap,
        DatePicker,
        LightButton,
        InputSelect,
        MapsSchedulesStopInfo,
        MapsSchedulesRouteRunOn,
        MapsSchedulesRouteScheduleList
    },

    setup() {
        const page = usePage();
        const routeStore = useRouteStore();
        const dayOfTravelPicker = ref();
        const selectedSchedule = computed(() => routeStore.selectedSchedule);
        const selectedStops = computed(() => routeStore.selectedStops);
        const currentRoute = computed(() => routeStore.routes.current);
        const routeScroll = computed(() => routeStore.routes.scroll);
        const calendarIsShowing = ref(false);

        const noServiceMessageHTML = computed(() => {
            if (! page?.props?.global?.routes?.no_service_available_message) {
                return '<p>No service for the selected Date of Travel and Direction of Travel.</p>';
            }

            return page.props.global.routes.no_service_available_message;
        });

        const noServiceMessageHTMLSpanish = computed(() => {
            if (! page?.props?.global?.routes?.no_service_available_message_spanish) {
                return '<p>No hay servicio para la fecha de viaje y dirección de viaje seleccionadas.</p>';
            }

            return page.props.global.routes.no_service_available_message_spanish;
        });

        const mapShape = computed(function () {
            if (! selectedSchedule.value?.shape) {
                return null;
            }

            return {
                ...selectedSchedule.value.shape,
                stops: selectedSchedule.value.shape.stops,
                originStop: selectedSchedule.value.shape.stops[0],
                destStop: selectedSchedule.value.shape.stops[selectedSchedule.value.stops.length - 1],
            };
        });

        function onMapReady(mapRef) {
            setGoogleApi(mapRef.value.api);

            // @todo: zoom to route here
        }

        function onMarkerClicked(marker) {
            routeStore.setSelectedStops([marker.extra_options.stop]);
        }

        function onDatePickerPopoverOpen() {
            calendarIsShowing.value = true;
            const popoverEl = dayOfTravelPicker.value.popoverRef.$el;

            // Update aria attributes for each day in the calendar
            popoverEl
                .querySelectorAll('.vc-day')
                .forEach((el) => el.setAttribute('aria-pressed', 'false'));

            popoverEl
                .querySelector('.vc-highlight-content-solid')
                ?.setAttribute('aria-pressed', 'true');

            // Update aria attributes for each month when the month selector is open
            if (popoverEl.querySelector('[data-id]')) {
                popoverEl
                    .querySelectorAll('[data-id]')
                    .forEach((el) => {
                        const dateStr = el.getAttribute('data-id')?.split('.');

                        if (dateStr.length === 2) {
                            const date = new Date(parseInt(dateStr[0]), parseInt(dateStr[1]) - 1, 1, 0, 0, 0);
                            const month = date.toLocaleString('default', { month: 'long' });

                            el.setAttribute('aria-label', `${month} ${date.getFullYear()}`);
                        }
                    })

                popoverEl
                    .querySelectorAll('[data-id]:not(.is-active)')
                    .forEach((el) => el.setAttribute('aria-current', 'false'));

                popoverEl
                    .querySelector(`[data-id].is-active`)
                    ?.setAttribute('aria-current', 'true');
            }
        }

        function onDatePickerPopoverClose(){
            calendarIsShowing.value = false;
        }

        function setDate(date) {
            if (!date) {
                return;
            }
            routeStore.setDayOfTravelDate(date);
        }

        return {
            routeStore,
            mapShape,
            routeScroll,
            currentRoute,
            selectedStops,
            selectedSchedule,
            dayOfTravelPicker,
            noServiceMessageHTML,
            noServiceMessageHTMLSpanish,
            calendarIsShowing,

            // Icons
            mdiAlert,
            mdiTable,
            mdiViewList,
            mdiArrowUp,
            mdiArrowDown,

            // Methods
            setDate,
            onMapReady,
            onMarkerClicked,
            onDatePickerPopoverOpen,
            onDatePickerPopoverClose,
        }
    },
});
</script>
<style>
.map-schedules-route-schedule .vc-light{
    --vc-day-content-hover-bg: #591769;
}

.map-schedules-route-schedule .vc-day-content:hover {
    color: white;
}
</style>
